import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import OfficeSectionsHeader from "../../components/OfficeSectionsHeader/OfficeSectionsHeader";
import Download from "../../assets/images/download.png";
import { saveAs } from "file-saver";
import { RootState, AppDispatch } from "../../redux/store";
import { CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  getOfficeFinanceRequests,
  getOfficeFinanceReport,
  setActiveOfficeFinanceTab,
  getOfficeAccountReceivablesPayee,
  getOfficeAccountReceivablesTopOverdueAccounts,
  getOfficeAccountReceivablesTotals,
  getOfficeAccountReceivables,
  getOfficeAnnualPlanTotals,
  getOfficeAnnualPlanRevenueByPlan,
  getOfficeFinanceReportCsv,
  getOfficeAnnualPlanReportCsv,
  getOfficeReportForMTD,
} from "../../redux/slices/FinanceSlice";
import ButtonTabs from "../../components/Tabs/ButtonTabs";
import FinanceDashboard from "./FinanceDashboard";
import AccountReceivable from "./AccountReceivable";
import AnnualPlan from "./AnnualPlan";
import { getMonthDropdownOptions, getMonthRange, getReportDateRange } from "../../utils";
import { getPdfOptions } from "../../utils/getPdfoption";
import { downloadFile } from "../../utils/fileDownloads";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { logger } from "../../utils/logger";
import FinancesReportPDF from "../AdminFinanceScreen/OfficesTab/FinancesReportPDF";
import CashFlowReportPDF from "../AdminFinanceScreen/OfficesTab/CashFlowReportPDF";
import { loadSecureZip } from "../../utils/loadSecureZip";
import { setSelectedOffice } from "../../redux/slices/OfficeSlice";
import { FinanceReportKeys } from "../../utils/constants";
import { FinanceBilledTo } from "../../models/finance.model";
import { UserType } from "../../services/user.service";
import FinancesReportOfficePDF from "../AdminFinanceScreen/OfficesTab/FinanceReportOfficePDF";
import CashFlowReportOfficePDF from "../AdminFinanceScreen/OfficesTab/CashFlowReportOfficePDF";

const html2pdf = require("html2pdf.js");
const log = logger.getLogger("ReportExportError");
const JSZip = require("jszip");

const FinanceScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const currentOffice = useSelector((state: RootState) => state.office.currentOffice);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const { officeFinanceReport, loading, activeOfficeTab: activeTab, officeSort } = useSelector((state: RootState) => state.finance);
  const date = new Date();
  const monthName = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const [monthOptions, setMonthOptions] = useState<string[]>(["january"]);
  const reportingMonths = monthOptions?.length ? [...monthOptions] : [];
  reportingMonths.shift();
  const today = moment();
  const currentMonthMTD =
    today.date() > 1 ? `${today.format("MMMM YYYY")} - MTD` : today.date() === 1 ? moment().subtract(1, "month").format("MMMM YYYY") : null;
  const [selectedMonth, setSelectedMonth] = useState<any>(currentMonthMTD);
  const [officeFinanceReportData, setOfficeFinanceReportData] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(false);
  const { financeReportTabData } = useSelector((state: RootState) => state.finance);
  const [showDownloadButton, setShowDownloadButton] = useState(false);

  date.setDate(1);
  const monthArray: string[] = [];
  for (let i = 0; i <= 11; i++) {
    monthArray.push(monthName[date.getMonth()] + " " + date.getFullYear());
    date.setMonth(date.getMonth() - 1);
  }

  const reportMonth = selectedMonth || moment().subtract(1, "month").format("MMMM YYYY");

  const fetchData = async (startDate: string, endDate: string, isMTD?: boolean, page = 0, size = 10) => {
    if (currentOffice?.officeId) {
      // dispatch(
      //   getOfficeFinanceReport({
      //     officeId: currentOffice?.officeId,
      //     startDate: new Date(startDate),
      //     endDate: new Date(endDate),
      //     mtd: isMTD ? "mtd" : undefined,
      //   })
      // );
      const response: any = await dispatch(
        getOfficeFinanceReport({
          officeId: currentOffice?.officeId,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          mtd: isMTD ? "mtd" : undefined,
        })
      );
      setOfficeFinanceReportData(response?.payload);
      dispatch(
        getOfficeFinanceRequests({
          officeId: currentOffice?.officeId,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          page: page,
          size: size,
          sort: officeSort.sortedBy,
        })
      );
    }
  };

  const downloadAsMTD = async (element: HTMLElement | null, SummaryPDF: string, monthYearKey: string) => {
    try {
      const myresponse: any = await dispatch(
        getOfficeReportForMTD({
          officeId: currentOffice?.officeId ?? "",
          monthYearKey: monthYearKey,
        })
      );

      const fileUrl = myresponse?.payload?.downloadUrl;
      if (!fileUrl) {
        throw new Error("No download URL received");
      }

      const res = await fetch(fileUrl);
      if (!res.ok) {
        throw new Error("Download failed with status " + res.status);
      }

      const blob = await res.blob();
      const existingZip = await loadSecureZip(blob);
      element?.classList.remove("hidden");

      const opt = getPdfOptions(myresponse?.payload?.fileName);
      const pdfBuffer = await html2pdf().set(opt).from(element).outputPdf("arraybuffer");

      existingZip.file(`${SummaryPDF}.pdf`, pdfBuffer);
      existingZip.generateAsync({ type: "blob" }).then((newBlob: any) => {
        downloadFile(newBlob, SummaryPDF + ".zip");
        setIsLoading(false);
      });
    } catch (error) {
      toast?.errorToast("Sorry, your request could not be processed. Please contact Docnote Support team.");
      setIsLoading(false);
    }
  };

  const handleMonthChange = (newMonth: string) => {
    setSelectedMonth(newMonth);
  };

  const handleTabChange = (index: number) => {
    dispatch(setActiveOfficeFinanceTab({ index }));
  };

  useEffect(() => {
    if (!currentOffice?.officeId) return;

    const options = getMonthDropdownOptions(currentOffice.createdDate);
    setMonthOptions(options);

    const isMTD = selectedMonth.includes("MTD");
    const [monthLabel, yearLabel] = selectedMonth.replace(" - MTD", "").split(" ");
    const monthIdx = monthName.indexOf(monthLabel);
    const { firstDay, lastDay } = getReportDateRange(monthIdx, selectedMonth, isMTD);
    if (!firstDay || !lastDay) return;

    fetchData(firstDay, lastDay, isMTD);
    dispatch(getOfficeAccountReceivablesTotals({ officeId: currentOffice.officeId }));
    dispatch(getOfficeAccountReceivablesPayee({ officeId: currentOffice.officeId }));
    dispatch(getOfficeAccountReceivablesTopOverdueAccounts({ officeId: currentOffice.officeId }));
    dispatch(getOfficeAccountReceivables({ officeId: currentOffice.officeId, page: 0 }));
    dispatch(getOfficeAnnualPlanTotals({ officeId: currentOffice.officeId }));
    dispatch(getOfficeAnnualPlanRevenueByPlan({ officeId: currentOffice.officeId }));
    dispatch(setSelectedOffice(currentOffice));
  }, [currentOffice, selectedMonth, dispatch]);

  const noRecordFound = !(
    officeFinanceReport?.totalRevenue ||
    officeFinanceReport?.discountsProvided ||
    officeFinanceReport?.feesOwedToDs ||
    officeFinanceReport?.netIncome ||
    officeFinanceReport?.dsCollected ||
    officeFinanceReport?.collectedByOffice ||
    officeFinanceReport?.outstandingPayments ||
    officeFinanceReport?.totalHstApplicableRevenue ||
    officeFinanceReport?.hstCollected
  );

  const buttonTabOptions = ["FINANCE DASHBOARD", "ANNUAL PLAN", "ACCOUNTS RECEIVABLE"];
  const buttonTabComponents = [
    <FinanceDashboard key="dashboard-tab" noRecordFound={noRecordFound} />,
    <AnnualPlan key="annual-tab" />,
    <AccountReceivable key="acr-tab" />,
  ];
  useEffect(() => {
    // use officeFinanceReport obj at the place of financeReportTabData
    if (officeFinanceReport) {
      const reportData: any = officeFinanceReport;
      const length = FinanceReportKeys.filter((item: string) => reportData[item] !== undefined && reportData[item] > 0).length;
      const length1 = officeFinanceReport?.billedTo?.filter((item: FinanceBilledTo) => item.amount > 0).length;
      setShowDownloadButton(!length && !length1 ? false : true);
    }
  }, [officeFinanceReport]);

  const downloadInvoiceAsPdf = async () => {
    try {
      setIsLoading(true);
      const isMTD = selectedMonth.includes("MTD");
      const splitMonth = reportMonth.split(" ");
      const month = monthName.indexOf(splitMonth[0]);
      const year = splitMonth[1];
      const { firstDay, lastDay } = getReportDateRange(month, reportMonth, isMTD);
      const monthYearKey = `${splitMonth[0]}_${splitMonth[1]}`;

      let accountingCsvData = "";
      let annualPlanCsvData = "";
      const element = document.getElementById("finance-report-container");
      const fileMonth = `${moment([year, month]).format("MMMM")}${moment([year, month]).format("YYYY")}`;
      const officeName = currentOffice?.name ? currentOffice?.name?.split(" ").join("") : "";
      const SummaryPDF = `${officeName}Summary${fileMonth}`;
      const isAboveJune2025 = year > "2025" || (year === "2025" && month > monthName.indexOf("June"));
      if (currentOffice?.officeId && !isAboveJune2025) {
        const requestParams = {
          officeId: currentOffice?.officeId,
          startDate: new Date(firstDay),
          endDate: new Date(lastDay),
        };
        const accountingReportResponse: any = await dispatch(getOfficeFinanceReportCsv(requestParams));
        const annualPlanReportResponse: any = await dispatch(getOfficeAnnualPlanReportCsv(requestParams));

        const accountingData = accountingReportResponse?.payload?.toString().split(/\n/);
        if (accountingData?.length) {
          const data = accountingReportResponse?.payload?.toString().split(/\n/);
          const headers = data[0];
          const csvRows = [];
          csvRows.push(headers);

          data.slice(1).map((item: string) => {
            csvRows.push(item);
            return true;
          });
          accountingCsvData = csvRows.join("\n");
        }

        const annualPlanData = annualPlanReportResponse?.payload?.toString().split(/\n/);
        if (annualPlanData?.length) {
          const data = annualPlanReportResponse?.payload?.toString().split(/\n/);
          const headers = data[0];
          const csvRows = [];
          csvRows.push(headers);
          data.slice(1).map((item: string) => {
            csvRows.push(item);
            return true;
          });
          annualPlanCsvData = csvRows.join("\n");
        }

        const AccountingCSV = `${officeName}AccountingCSV${fileMonth}.csv`;
        const AnnualPlanCSV = `${officeName}AnnualPlanCSV${fileMonth}.csv`;
        const zipName = `${officeName}MonthlyReport${fileMonth}.zip`;
        element?.classList.remove("hidden");
        const opt = getPdfOptions(SummaryPDF);
        const zip = new JSZip();
        zip.file(`${SummaryPDF}.pdf`, html2pdf().set(opt).from(element).outputPdf("arraybuffer"));
        if (accountingCsvData) {
          zip.file(`${AccountingCSV}`, new Blob([accountingCsvData], { type: "text/csv" }));
        }
        if (annualPlanCsvData) {
          zip.file(`${AnnualPlanCSV}`, new Blob([annualPlanCsvData], { type: "text/csv" }));
        }
        zip.generateAsync({ type: "blob" }).then((blob: any) => {
          saveAs(blob, `${zipName}`);
        });
        setIsLoading(false);
      } else {
        downloadAsMTD(element, SummaryPDF, monthYearKey);
      }
    } catch (err) {
      setIsLoading(false);
      toast?.openToast("Download report failed. Try again!");
      log.warn(JSON.stringify(err));
    }
  };

  return (
    <>
      <main>
        <div
          className="container mx-auto p-8"
          style={{
            minHeight: "calc(100vh - 96px)",
          }}
        >
          <div className="block xl:flex justify-between items-end">
            <div className="">
              <OfficeSectionsHeader showMeta={true} />
            </div>
          </div>
          {loading ? (
            <CircularProgress size={40} color={"info"} style={{ marginLeft: "50%", marginTop: "10%" }} />
          ) : (
            // !noData?
            <>
              <div className="relative">
                <div className="mb-5 flex items-center">
                  {isLoading ? (
                    <CircularProgress size={22} className="ml-5" />
                  ) : showDownloadButton &&
                    !(
                      currentUser?.userType === UserType.User ||
                      currentUser?.userType === UserType.Accounting ||
                      currentUser?.userType === UserType.OfficeAdmin
                    ) ? (
                    <img src={Download} alt="Download Icon" className="w-6 h-6 cursor-pointer" onClick={downloadInvoiceAsPdf} />
                  ) : null}
                </div>
                <ButtonTabs
                  components={buttonTabComponents}
                  options={buttonTabOptions}
                  activeTab={activeTab}
                  onClickTab={handleTabChange}
                  fromOffice={true}
                />
                {activeTab === 0 ? (
                  <div className="absolute top-0 right-0 z-20 block lg:flex items-center mb-6 space-x-0 lg:space-x-3 space-y-2 lg:space-y-0">
                    <div className="flex justify-between items-center space-x-3">
                      <select
                        onChange={e => {
                          handleMonthChange(e.target.value);
                        }}
                        value={selectedMonth}
                        className="general-select leading-5 w-44"
                      >
                        {[...(currentMonthMTD ? [currentMonthMTD] : []), ...reportingMonths.filter(m => m !== currentMonthMTD)].map(
                          (ele: string, index: number) => {
                            return (
                              <option key={index} value={ele}>
                                {ele}
                              </option>
                            );
                          }
                        )}
                      </select>
                    </div>
                  </div>
                ) : null}
              </div>
              {/* {!officeFinanceReport?.billedTo?.length && !officeFinanceRequests?.length ? (
                <DataNotFound image={NotFound} text="No financial records found" />
              ) : null} */}
            </>
          )}
        </div>
        <div className="opacity-0">
          <div id="finance-report-container" className="lg:grid-cols-1 hidden">
            <FinancesReportOfficePDF isPdf={true} selectedMonth={reportMonth} fetchData={officeFinanceReportData} />
            <CashFlowReportOfficePDF isPdf={true} selectedMonth={reportMonth} fetchData={officeFinanceReportData} />
          </div>
        </div>
      </main>
    </>
  );
};
export default FinanceScreen;
